import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getMoroccoNow } from "@majourneys/shared";
import { useTripBookingAdapter } from "../config";
import type { OfferLookup, TripStopOffer } from "../lib/trip-booking";

/**
 * The packages behind a set of activities, loaded through the host.
 *
 * Returns a lookup that answers `undefined` while a slug is loading and `null`
 * when it failed or cannot be booked, which is the contract `dayBookingPlan`
 * reads. Without a host adapter there is nothing to load and the lookup is
 * undefined, so the days quote the catalogue estimate.
 *
 * Keyed per slug and language, so two days sharing a stop, and the totals
 * card under them, read one request rather than three.
 */
export function useStopOffers(slugs: readonly string[]): OfferLookup | undefined {
  const adapter = useTripBookingAdapter();
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "en";
  const results = useQueries({
    queries: slugs.map((slug) => ({
      queryKey: ["trip-stop-offer", slug, lang] as const,
      queryFn: () => adapter!.fetchOffer(slug, lang),
      enabled: adapter !== null,
      staleTime: 5 * 60 * 1000,
      retry: 1,
    })),
  });

  // useQueries hands back a new array every render; key the lookup on what
  // the queries actually hold so consumers do not recompute for nothing.
  const signature = results.map((r) => `${r.status}:${r.dataUpdatedAt}`).join("|");
  return useMemo(() => {
    if (!adapter) return undefined;
    const bySlug = new Map<string, TripStopOffer | null | undefined>();
    slugs.forEach((slug, i) => {
      const r = results[i];
      if (!r || r.status === "pending") bySlug.set(slug, undefined);
      else if (r.status === "error") bySlug.set(slug, null);
      else bySlug.set(slug, r.data ?? null);
    });
    return (slug: string) => bySlug.get(slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, signature, slugs.join("|")]);
}

/**
 * Today in Morocco as YYYY-MM-DD, or null until the browser has mounted.
 *
 * Read after mount on purpose: the server renders in UTC and the guest
 * hydrates in their own zone, so a "today" read during render can disagree
 * between the two and throw the page away (React #418).
 */
export function useMoroccoToday(): string | null {
  const [today, setToday] = useState<string | null>(null);
  useEffect(() => {
    setToday(getMoroccoNow().date);
  }, []);
  return today;
}
